/**
 * Shared elevation-profile map marker.
 *
 * Replaces the several duplicated module-global `elevationMarker`
 * mapboxgl.Marker singletons scattered across the elevation renderers. Because
 * multiple profiles can be mounted at once (e.g. planner + hike map), a single
 * module-global marker is a cross-instance hazard: one profile's hover would
 * move/remove another's marker. Prefer {@link createElevationMarker}, which
 * returns an isolated `{ show, hide, destroy }` controller per caller.
 */
import mapboxgl from 'mapbox-gl';

export interface ElevationMarkerOptions {
  /** CSS class applied to the marker element. Defaults to `elevation-marker`. */
  className?: string;
  /** Optional mapboxgl.Marker options (anchor, offset, …). */
  markerOptions?: mapboxgl.MarkerOptions;
}

export interface ElevationMarkerController {
  /** Show (or move) the marker at the given `[lng, lat]`. */
  show(lngLat: [number, number] | mapboxgl.LngLatLike): void;
  /** Remove the marker from the map (keeps it reusable). */
  hide(): void;
  /** Permanently remove the marker; call on unmount. */
  destroy(): void;
}

function buildMarkerElement(className: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = className;
  return el;
}

/**
 * Create an isolated elevation-marker controller.
 *
 * @param getMap Accessor returning the map to attach to (or null/undefined if
 *   no map is available yet). Called lazily on each `show`, so it tolerates the
 *   map being created after the controller.
 */
export function createElevationMarker(
  getMap: () => mapboxgl.Map | null | undefined,
  options: ElevationMarkerOptions = {}
): ElevationMarkerController {
  const className = options.className ?? 'elevation-marker';
  let marker: mapboxgl.Marker | null = null;

  const show = (lngLat: [number, number] | mapboxgl.LngLatLike): void => {
    const map = getMap();
    if (!map || typeof map.getCenter !== 'function') return;

    if (!marker) {
      marker = new mapboxgl.Marker(
        options.markerOptions
          ? { element: buildMarkerElement(className), ...options.markerOptions }
          : { element: buildMarkerElement(className) }
      );
    }

    try {
      marker.setLngLat(lngLat).addTo(map);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error adding elevation marker to map:', error);
      hide();
    }
  };

  const hide = (): void => {
    if (marker) marker.remove();
  };

  const destroy = (): void => {
    if (marker) {
      marker.remove();
      marker = null;
    }
  };

  return { show, hide, destroy };
}

// ---------------------------------------------------------------------------
// Convenience module-global controller.
//
// For the common single-profile case, `showElevationMarker` / `hideElevationMarker`
// wrap one shared controller keyed by the map instance. When mounting multiple
// profiles simultaneously, use `createElevationMarker` instead.
// ---------------------------------------------------------------------------

let globalController: ElevationMarkerController | null = null;
let globalMap: mapboxgl.Map | null = null;

/**
 * Show a shared marker at `lngLat` on `map`. If a different map is passed, the
 * previous marker is destroyed first to avoid orphaning it on the old map.
 */
export function showElevationMarker(
  map: mapboxgl.Map,
  lngLat: [number, number] | mapboxgl.LngLatLike,
  options?: ElevationMarkerOptions
): void {
  if (globalController && globalMap !== map) {
    globalController.destroy();
    globalController = null;
  }
  globalMap = map;
  if (!globalController) {
    globalController = createElevationMarker(() => globalMap, options);
  }
  globalController.show(lngLat);
}

/** Hide the shared marker (see {@link showElevationMarker}). */
export function hideElevationMarker(): void {
  if (globalController) globalController.hide();
}
